import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Store, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("farmersmarketmdu@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (ready && user) navigate("/", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-sidebar text-white p-12 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-sidebar-accent/20 blur-3xl" />
        <div className="absolute bottom-0 -left-24 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-sidebar-accent shadow-xl">
            <Store className="h-6 w-6" />
          </div>
          <div>
            <div className="font-display text-2xl font-bold">SSACC</div>
            <div className="text-sm text-white/60">Saravana Stores Accounts</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-bold leading-tight">
            Supplier & payroll<br />accounting, simplified.
          </h1>
          <p className="mt-4 text-white/70 max-w-md">
            Purchase entries, multi-bill payments with adjustments, day ledger, employee salary and
            advance tracking — all in one professional console.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-white/60">
            <ShieldCheck className="h-4 w-4" /> Secured with encrypted admin login
          </div>
        </div>
        <div className="relative text-xs text-white/40">© {new Date().getFullYear()} Saravana Stores</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Store className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-xl font-bold">SSACC</div>
              <div className="text-xs text-muted-foreground">Saravana Stores</div>
            </div>
          </div>
          <h2 className="font-display text-2xl font-bold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Enter your admin credentials to continue.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5" data-testid="login-email" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5" data-testid="login-password" placeholder="••••••••" required />
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full mt-6 h-11" data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
